import { inject, injectable } from 'inversify';
import { TYPES as OFFLINE_TYPES } from '../ioc/types';
import type { OfflineDetectionService } from '../services/OfflineDetection.service';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import type { OfflineUtilsService } from '../services/OfflineUtils.service';
import { TYPES } from '@/engine/ioc';
import type { CaseDataProvider } from '@/engine/app/CaseDataProvider';
import type { DownloadCaseForOfflineUseCase } from '../services/DownloadCaseForOffline.usecase';

@injectable()
export class DownloadForOfflineButtonController {
  private readonly _offlineDetectionService: OfflineDetectionService;

  private readonly _offlineUtilsService: OfflineUtilsService;

  private readonly _caseDataProvider: CaseDataProvider;

  private readonly _downloadCaseForOfflineUseCase: DownloadCaseForOfflineUseCase;

  @observable
  private _isDownloading = false;

  @observable
  private _isCurrentCaseAvailableOffline = false;

  public constructor(
    @inject(OFFLINE_TYPES.OfflineDetectionService) offlineDetectionService: OfflineDetectionService,
    @inject(OFFLINE_TYPES.OfflineUtilsService) offlineUtilsService: OfflineUtilsService,
    @inject(TYPES.CaseDataProvider) caseDataProvider: CaseDataProvider,
    @inject(OFFLINE_TYPES.DownloadCaseForOfflineUseCase) downloadCaseForOfflineUseCase: DownloadCaseForOfflineUseCase
  ) {
    this._offlineDetectionService = offlineDetectionService;
    this._offlineUtilsService = offlineUtilsService;
    this._caseDataProvider = caseDataProvider;
    this._downloadCaseForOfflineUseCase = downloadCaseForOfflineUseCase;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this.init();
    makeObservable(this);
  }

  public async init(): Promise<void> {
    const caseId = this._caseDataProvider.getCaseId();
    const packageId = this._caseDataProvider.getPackageId();

    const isCaseAvailableOffline = await this._offlineUtilsService.isCaseAvailableOffline(caseId, packageId);

    runInAction(() => {
      this._isCurrentCaseAvailableOffline = isCaseAvailableOffline;
    });
  }

  @computed
  public get isCurrentCaseAvailableOffline(): boolean {
    return this._isCurrentCaseAvailableOffline;
  }

  @computed
  public get isDownloading(): boolean {
    return this._isDownloading;
  }

  public async downloadCurrentCaseForOffline(): Promise<void> {
    const caseId = this._caseDataProvider.getCaseId();
    const packageId = this._caseDataProvider.getPackageId();
    if (this.isCurrentCaseAvailableOffline || this.isDownloading) {
      return;
    }

    try {
      this._isDownloading = true;
      await this._downloadCaseForOfflineUseCase.execute({
        caseId,
        packageId
      });

      runInAction(() => {
        this._isCurrentCaseAvailableOffline = true;
      });
    } catch (error: unknown) {
      console.error(error);
    } finally {
      this._isDownloading = false;
    }
  }
}
